import dayjs from 'dayjs';
import styled from 'styled-components';
import {usePaymentHistory} from '@/api/payments';
import {Typography, typographyStyle} from '@/components/Typography';
import {FULL_DATE_FORMAT, PAY_HISTORY_STATUS} from '@/constants';
import {useBreakpoints, useSession} from '@/hooks';
import {PaymentHistory} from '@/interface';
import {
  DashRoundedBox, Divider, Flexbox, FlexFullGrow, GrayRoundedBox, PaddingBox, SimpleListBox
} from '@/styled-components';
import {palette} from '@/styles';
import {getCardName, getCardShortNumber, withComma} from '@/utils';
import {Box} from './StyledComponents';

interface ListProps {
  items: PaymentHistory[];
}

const getCardNumber = (number?: string) => {
  if (!number?.length) {
    return '';
  }

  return `(${getCardShortNumber(number)})`;
};

export const PayHistory = () => {
  const {user} = useSession();
  const {isMobile} = useBreakpoints();
  const {data, isPending} = usePaymentHistory(user?.id);

  const isEmpty = !data?.length;

  if (isPending || isEmpty) {
    return (
      <Box>
        <Typography variant="bold14">결제 이력</Typography>
        <DotBox>
          <Typography variant="bold16" color={palette.gray400}>
            {isPending
              ? '결제 이력을 불러오는 중입니다.'
              : '결제 이력이 없습니다.'}
          </Typography>
        </DotBox>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="bold14">결제 이력</Typography>
      {isMobile ? (
        <HistoryBlocks items={data} />
      ) : (
        <HistoryTable items={data} />
      )}
      <SimpleListBox>
        <ul>
          <li>2년 이내 결제 이력만 표시합니다.</li>
          <li>카드 결제 건에 대해서만 영수증 확인이 가능합니다.</li>
        </ul>
      </SimpleListBox>
    </Box>
  );
};

const HistoryBlocks = ({items}: ListProps) => {
  return (
    <Box>
      {items.map((i, idx) => (
        <GrayBox key={idx}>
          <Flexbox>
            <Typography
              color={PAY_HISTORY_STATUS[i.status].color}
              fontWeight={700}
            >
              {PAY_HISTORY_STATUS[i.status].label}
            </Typography>
            <FlexFullGrow />
            <Typography color={palette.gray300}>
              {dayjs(i.authenticatedAt).format(`${FULL_DATE_FORMAT} HH:mm`)}
            </Typography>
          </Flexbox>
          <PaddingBox bottom={8}>
            <Flexbox>
              <Typography variant="bold16" color={palette.gray800}>
                {i.orderName}
              </Typography>
              <FlexFullGrow />
              <Typography variant="bold16" color={palette.gray600}>
                {withComma(i.totalAmount)}원
              </Typography>
            </Flexbox>
          </PaddingBox>
          <Divider />
          <Flexbox>
            <Typography color={palette.gray600}>
              {getCardName(i.cardCompany)}
              {getCardNumber(i.cardNumber)}
            </Typography>
            <FlexFullGrow />
            <CardRecipt receiptUrl={i.receiptUrl} />
          </Flexbox>
        </GrayBox>
      ))}
    </Box>
  );
};

const HistoryTable = ({items}: ListProps) => {
  return (
    <Table>
      <tbody>
        {items.map((i, idx) => (
          <tr key={idx}>
            <td>
              <Typography color={PAY_HISTORY_STATUS[i.status].color} nowrap>
                {PAY_HISTORY_STATUS[i.status].label}
              </Typography>
            </td>
            <td>
              <Typography nowrap>{i.orderName}</Typography>
            </td>
            <td>
              <Typography nowrap>{withComma(i.totalAmount)}원</Typography>
            </td>
            <td>
              <Typography color={palette.gray400}>
                {getCardName(i.cardCompany)}
                {getCardNumber(i.cardNumber)}
              </Typography>
            </td>
            <td>
              <Typography color={palette.gray400}>
                {dayjs(i.authenticatedAt).format(`${FULL_DATE_FORMAT} HH:mm`)}
              </Typography>
            </td>
            <td>
              <CardRecipt receiptUrl={i.receiptUrl} />
            </td>
          </tr>
        ))}
      </tbody>
    </Table>
  );
};

const CardRecipt = ({receiptUrl}: {receiptUrl?: string}) => {
  const {isMobile} = useBreakpoints();

  return !!receiptUrl?.length ? (
    <a
      href={receiptUrl}
      target="_blank"
      rel="noopener noreferrer"
      style={{
        color: isMobile ? palette.gray800 : palette.blue400,
        textDecoration: 'underline',
        whiteSpace: 'nowrap',
      }}
    >
      영수증 보기
    </a>
  ) : (
    <span>-</span>
  );
};

const DotBox = styled(DashRoundedBox)`
  display: flex;
  justify-content: center;
  padding: 24px;
`;

const GrayBox = styled(GrayRoundedBox)`
  display: flex;
  flex-direction: column;
  padding: 16px 16px 8px;
  gap: 8px;
  ${{...typographyStyle.medium14}}
`;

const Table = styled.table`
  border-collapse: collapse;
  border: 1px solid ${palette.gray150};
  ${{...typographyStyle.medium14}}
  margin: 8px 0;
  td {
    border: 1px solid ${palette.gray150};
    padding: 8px 12px;
  }
`;
